/**
 * What a REFUSED managed-screen assignment means — and, crucially, whether the caller
 * should try again at all.
 *
 * `UpdateWorkflowScreens` answers per step, and a refusal used to be reported as one
 * undifferentiated failure: "FAILED for 1 user task(s): <stepId> — <backend message>".
 * That reads to the orchestrator exactly like "the screen was not created", which is the
 * cue to REBUILD — and rebuilding is the one thing that must not happen here, because a
 * second full generation is what produced duplicate task screens and a scrambled
 * step↔screen mapping (§38).
 *
 * KOA-15840 added a second, benign reason for a refusal: the target screen is HELD OPEN.
 * Either somebody has it in the classic in-use sense, or a live collaboration session owns
 * it (the session is the only legitimate writer while it lives, so the whole-screen write
 * is refused rather than racing it). Both are transient and neither says anything about the
 * process being wrong — the SAME script succeeds once the screen is closed.
 *
 * So the classification here is not cosmetic. It decides between two opposite instructions
 * to the caller: "the process is broken, look at the reason" versus "nothing is wrong, the
 * screen is busy — close it and re-send this same script unchanged".
 */

/** Why a step's managed screen was not (re)generated. */
export type AssignmentFailureKind =
  /** The screen is open elsewhere — in-use by another user, or owned by a live collab session. Transient; retry the same script. */
  | "screen_held_open"
  /** Anything else: a genuine refusal whose reason must be read (missing step, validation, exception). */
  | "refused";

/** One step's refusal, with the backend's own words preserved. */
export interface IAssignmentFailure {
  processId: string;
  stepId: string;
  message: string;
  kind: AssignmentFailureKind;
}

/**
 * Distinctive fragments of the two backend refusals that mean "the screen is held open".
 * Matched case-insensitively on the message the backend sent, never on a status code —
 * the wire carries `isSuccess:false` plus prose, and prose is all there is to key on.
 * Both fragments are ASCII, so `toLowerCase()` is locale-independent here.
 */
const SCREEN_HELD_OPEN_FRAGMENTS: readonly string[] = ["in use by another user", "live collaboration session"];

/** Classify one refusal message. An empty/unknown message is a genuine refusal, never a retryable one. */
export function classifyAssignmentFailure(message: string | null | undefined): AssignmentFailureKind {
  const text: string = (message ?? "").toLowerCase();
  if (text.length === 0) {
    return "refused";
  }
  const heldOpen: boolean = SCREEN_HELD_OPEN_FRAGMENTS.some((fragment: string): boolean => text.includes(fragment));
  return heldOpen ? "screen_held_open" : "refused";
}

/** The two buckets a set of refusals splits into, plus the sentence that describes them. */
export interface IAssignmentFailureSummary {
  /** Transient: the screen is open somewhere. Re-sending the same script works once it is closed. */
  heldOpen: IAssignmentFailure[];
  /** Genuine: the reason must be read and acted on. */
  refused: IAssignmentFailure[];
  /** Ready-to-append sentence(s) for the action's message. Empty when there were no refusals. */
  note: string;
}

/**
 * Split refusals into the two buckets and word each one for the CALLER's next move.
 *
 * The held-open sentence deliberately names the remedy and forbids the rebuild: the caller
 * is an agent that will otherwise infer "screen missing → build it again".
 */
export function summariseAssignmentFailures(failures: readonly IAssignmentFailure[]): IAssignmentFailureSummary {
  const heldOpen: IAssignmentFailure[] = failures.filter(
    (failure: IAssignmentFailure): boolean => failure.kind === "screen_held_open"
  );
  const refused: IAssignmentFailure[] = failures.filter(
    (failure: IAssignmentFailure): boolean => failure.kind === "refused"
  );

  const parts: string[] = [];
  if (heldOpen.length > 0) {
    parts.push(
      ` NOT GENERATED YET for ${heldOpen.length} user task(s) because their screen is open elsewhere ` +
        `(${heldOpen.map((failure: IAssignmentFailure): string => failure.stepId).join(", ")}). ` +
        `The process itself is fine and nothing is half-built: close the screen in the designer (or ask the ` +
        `other editor to), then re-send this SAME script unchanged. Do NOT rebuild the process and do NOT ` +
        `create another screen.`
    );
  }
  if (refused.length > 0) {
    parts.push(
      ` FAILED for ${refused.length} user task(s): ` +
        refused.map((failure: IAssignmentFailure): string => `${failure.stepId} — ${failure.message}`).join("; ")
    );
  }

  return { heldOpen, refused, note: parts.join("") };
}
